import {useEffect, useRef, useState, type RefObject} from 'react';
import type {PlayerRef} from '@remotion/player';
import {Button, ContentDialog, Chip, TextArea, RadixDialogTitle, RadixDialogDescription} from '../../vendor/ds-react';
import {CloudUpload, FolderOpen, GiPaperWaterdrop, Movie} from '../../vendor/src/shared/ui/generated-web-icons/package-shim.js';
import {useAssets, useAllItems, useSelectedItems, useCurrentStateAsRef} from '../../vendor/src/features/remotion-editor/editor/utils/use-context';
import {selectedCompanyContinueClip} from '../../protocol/continue-clip';
import {readCompanyVideoDraft, type CompanyVideoDraft, type VideoDraftInput, type VideoDraftSource} from '../../protocol/video-draft';
import {requestVideoSource, stageVideoDraft} from '../host/video-draft';
import {stageVideoSourceUpload} from '../host/video-source-upload';
import {readVideoSourceUpload} from '../../protocol/video-source-upload';

type Props = {open:boolean; onClose:()=>void; playerRef:RefObject<PlayerRef|null>; projectId?:number;initialDraft?:VideoDraftInput};
const movements = ['자연스럽게', '활기차게', '우아하게', '장난스럽게', '극적으로'];
const messageOf = (error:unknown) => error instanceof Error ? error.message : '요청을 전달하지 못했습니다. 다시 시도해 주세요.';

/** Each opening owns its async results; old responses cannot close a new session. */
export function NativeVideoDraftDialog({open, ...props}:Props) {
  return open ? <VideoDraftSession {...props}/> : null;
}

function VideoDraftSession({onClose, playerRef, initialDraft}:Omit<Props,'open'>) {
  const {assets} = useAssets();
  const {items} = useAllItems();
  const {selectedItems} = useSelectedItems();
  const current = useCurrentStateAsRef();
  const [source, setSource] = useState<VideoDraftSource|null>(initialDraft?.source??null);
  const [selectionSignature,setSelectionSignature]=useState<string|null>(initialDraft?.expectedSourceSignature??null);
  const [endAssetId,setEndAssetId]=useState<string|undefined>(initialDraft?.endAssetId);
  const [endSignature,setEndSignature]=useState<string|null>(initialDraft?.expectedEndSignature??null);
  const [movement, setMovement] = useState(initialDraft?.movement??0);
  const [customPrompt, setCustomPrompt] = useState(initialDraft?.customPrompt??'');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const active = useRef(true);
  const pending = useRef(false);
  useEffect(() => {active.current = true; return () => {active.current = false;};}, []);
  const selectedClip = selectedCompanyContinueClip({items}, selectedItems);
  const images = Object.values(assets).filter(asset => asset.type === 'image');

  const resolve = (candidate:VideoDraftSource, expected=selectionSignature,endId=endAssetId,expectedEnd:string|null=endSignature):{input:VideoDraftInput;draft:CompanyVideoDraft} => {
    const document = current.current.undoableState;
    const clip = candidate.type === 'cut' ? document.items[candidate.clipId] : null;
    const from = clip ? clip.from + clip.durationInFrames : playerRef.current?.getCurrentFrame();
    if(from === undefined) throw new Error('재생 위치를 확인한 뒤 다시 시도해 주세요.');
    const input = {source:candidate, movement, customPrompt, from, fps:document.fps,...(endId?{endAssetId:endId,...(expectedEnd?{expectedEndSignature:expectedEnd}:{})}:{}),...(expected?{expectedSourceSignature:expected}:{})};
    return {input, draft:readCompanyVideoDraft(input, document)};
  };
  let preview:CompanyVideoDraft|null = null;
  let validation = '';
  if(source) {
    try {preview = resolve(source).draft;}
    catch(error) {validation = messageOf(error);}
  }
  const choose = (candidate:VideoDraftSource) => {
    if(pending.current) return;
    try {const selected=resolve(candidate,null);setSelectionSignature(selected.draft.sourceSignature); setSource(candidate); setMessage('');}
    catch(error) {setMessage(messageOf(error));}
  };
  const chooseEnd=(id:string)=>{
    if(pending.current)return;
    if(!id){setEndAssetId(undefined);setEndSignature(null);setMessage('');return;}
    try{if(!source)throw Error('시작할 원본을 먼저 선택해 주세요.');const selected=resolve(source,selectionSignature,id,null);setEndAssetId(id);setEndSignature(selected.draft.endFrame!.signature);setMessage('');}
    catch(error){setMessage(messageOf(error));}
  };
  const chooseCut = (frame:'end'|'playhead') => {
    if(!selectedClip) {setMessage('타임라인에서 영상 또는 이미지 컷 하나를 선택해 주세요.'); return;}
    if(frame === 'end') {choose({type:'cut',clipId:selectedClip,frame}); return;}
    const playheadFrame = playerRef.current?.getCurrentFrame();
    if(playheadFrame === undefined) {setMessage('재생 위치를 확인한 뒤 다시 시도해 주세요.'); return;}
    choose({type:'cut',clipId:selectedClip,frame,playheadFrame});
  };
  const run = async (action:()=>Promise<void>, closeOnSuccess:boolean,successMessage='원본을 가져온 뒤 아래 현재 작업 이미지에서 선택해 주세요.') => {
    if(pending.current) return;
    pending.current = true; setBusy(true); setMessage('');
    try {
      await action();
      if(!active.current) return;
      if(closeOnSuccess) onClose();
      else setMessage(successMessage);
    } catch(error) {if(active.current) setMessage(messageOf(error));}
    finally {pending.current = false; if(active.current) setBusy(false);}
  };
  const submit = () => void run(async () => {
    if(!source) throw new Error('생성에 사용할 이미지나 컷을 선택해 주세요.');
    const {input} = resolve(source);
    await stageVideoDraft(input);
  }, true);
  const dropImage=(files:FileList|File[])=>{
    if(pending.current)return;
    if(files.length!==1){setMessage('이미지 파일 하나씩 끌어놓아 주세요.');return;}
    const file=files[0]!;
    try{readVideoSourceUpload({filename:file.name,blob:file});}catch(error){setMessage(messageOf(error));return;}
    void run(async()=>{
      const assetId=await stageVideoSourceUpload(file);
      if(!active.current)return;
      const candidate:VideoDraftSource={type:'image',assetId};
      const selected=resolve(candidate,null);
      setSelectionSignature(selected.draft.sourceSignature);setSource(candidate);
    },false,'끌어놓은 원본을 선택했습니다. 움직임과 요청을 확인해 주세요.');
  };

  return <ContentDialog open setOpen={next => {if(!next&&!pending.current) onClose();}} isShowHeaderNode={false} isDefaultFooterButtonsHidden
    contentClassName="w-[calc(100vw-48px)] max-w-[1280px] h-[min(784px,calc(100vh-48px))]">
    <div aria-label="영상 생성 이미지 끌어놓기" className="flex h-full min-h-0 w-full flex-col gap-xs"
      onDragOver={event=>{event.preventDefault();event.stopPropagation();event.dataTransfer.dropEffect=busy?'none':'copy';}}
      onDrop={event=>{event.preventDefault();event.stopPropagation();dropImage(event.dataTransfer.files);}}>
      <div className="flex flex-col gap-4xs">
        <RadixDialogTitle className="text-title-large text-sys-cont-neutral-default">AI 영상 만들기</RadixDialogTitle>
        <RadixDialogDescription className="text-body-medium text-sys-cont-neutral-default">원본과 움직임을 고르면 와카에서 콘티를 수정하고 비용을 확인한 뒤 영상을 만들 수 있습니다.</RadixDialogDescription>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-md overflow-y-auto md:grid-cols-2">
        <div className="flex min-h-0 min-w-0 flex-col gap-2xs overflow-y-auto">
          <div className="flex gap-2xs">
            <Button hierarchy="tertiary" size="small" disabled={busy} onClick={() => void run(() => requestVideoSource('upload'), false)} className="h-11 flex-1">
              <CloudUpload className="size-sm"/> 이미지 업로드
            </Button>
            <Button hierarchy="tertiary" size="small" disabled={busy} onClick={() => void run(() => requestVideoSource('library'), false)} className="h-11 flex-1">
              <FolderOpen className="size-sm"/> 원본 자료함
            </Button>
          </div>
          <p className="text-body-small text-sys-cont-neutral-light">여기에 50MB 이하 PNG, JPEG, WebP 이미지 하나를 끌어놓거나 업로드 창에서 선택하세요. 원본은 현재 와카 작업 저장소에 추가됩니다.</p>
          <p className="text-title-xsmall text-sys-cont-neutral-light">현재 작업 이미지 · 미사용 원본 포함</p>
          <div className="grid min-h-32 grid-cols-2 gap-2xs">
            {images.map(asset => <button key={asset.id} type="button" disabled={busy} aria-pressed={source?.type === 'image' && source.assetId === asset.id}
              onClick={() => choose({type:'image',assetId:asset.id})}
              className={`min-w-0 rounded-xs border p-2xs text-left ${source?.type === 'image' && source.assetId === asset.id ? 'border-blue-400' : 'border-sys-style-neutral-light'}`}>
              {asset.remoteUrl && <img src={asset.remoteUrl} alt="" loading="lazy" className="h-24 w-full object-contain"/>}
              <p className="truncate text-comp-accent-small">{asset.filename || asset.id}</p>
            </button>)}
            {!images.length && <p className="col-span-2 text-body-small">아직 이미지가 없습니다. 이미지 업로드나 원본 자료함에서 가져오세요.</p>}
          </div>
          <p className="text-title-xsmall text-sys-cont-neutral-light">선택한 컷{selectedClip ? ' · 원본 프레임을 고르세요' : ' · 영상 또는 이미지 컷 하나를 선택하세요'}</p>
          <Button hierarchy="tertiary" size="small" disabled={busy || !selectedClip} onClick={() => chooseCut('end')} className="h-11 w-full">
            <Movie className="size-sm"/> 선택한 컷의 끝 프레임
          </Button>
          <Button hierarchy="tertiary" size="small" disabled={busy || !selectedClip} onClick={() => chooseCut('playhead')} className="h-11 w-full">
            <Movie className="size-sm"/> 선택한 컷의 현재 프레임
          </Button>
          {source?.type === 'cut' && <p className="text-body-small">{preview?.sourceType === 'image' ? '자막·효과·자르기를 제외한 이미지 원본을 사용합니다.' : '자막·효과를 제외한 영상 원본 프레임을 사용합니다.'}</p>}
          {source?.type === 'cut' && <p className="text-body-small">{preview?.sourceType === 'image' ? '선택한 이미지 컷' : source.frame === 'end' ? '선택한 컷의 마지막 프레임' : `타임라인 ${source.playheadFrame} 프레임`} · 새 영상은 이 컷 뒤에 추가합니다.</p>}
        </div>
        <div className="bg-sys-style-neutral-inverse-light flex min-h-0 min-w-0 flex-col gap-sm overflow-y-auto rounded-xs p-sm">
          <div className="bg-sys-style-neutral-inverse-light h-[280px] shrink-0 overflow-hidden rounded-xs">
            {preview ? preview.sourceType === 'image' ? <img src={preview.remoteUrl} alt="선택한 생성 원본" className="h-full w-full object-contain"/>
              : <FramePreview key={`${preview.remoteUrl}:${preview.sourceFrameSeconds}`} url={preview.remoteUrl} seconds={preview.sourceFrameSeconds!}/>
              : <div className="flex h-full flex-col items-center justify-center gap-sm px-md text-center"><GiPaperWaterdrop className="size-4xl"/><p className="text-title-medium">영상으로 만들 원본을 선택하세요</p></div>}
          </div>
          <div className="flex flex-col gap-2xs">
            <label>종료 이미지 (선택)<select aria-label="영상 종료 이미지" disabled={busy||!source} value={endAssetId??''} onChange={e=>chooseEnd(e.target.value)} className="w-full rounded-xs border border-sys-style-neutral-light bg-sys-bg-surface-1 p-2xs"><option value="">종료 이미지 없이 생성</option>{images.map(asset=><option key={asset.id} value={asset.id}>{asset.filename||asset.id}</option>)}</select></label>
            {preview?.endFrame&&<><img src={preview.endFrame.remoteUrl} alt="영상 목표 종료 이미지" className="h-24 w-full object-contain"/><p className="text-body-small">시작·종료 두 이미지를 기준으로 Omni 1.1 · 720p로 요청합니다. 콘티에서 제작 방식과 비용을 확인하세요. 실제 끝 모습은 생성 결과에서 확인해야 합니다.</p></>}
          </div>
          <div className="flex flex-col gap-2xs">
            <p className="text-title-xsmall text-sys-cont-neutral-light">움직임 스타일</p>
            <div className="flex flex-wrap gap-2xs">{movements.map((label, index) => <Chip key={label} size="lg" style="filled" selected={movement === index} labelNode={label}
              className={busy ? 'pointer-events-none opacity-60' : ''} onClick={() => {if(!pending.current) setMovement(index);}}/>)}</div>
          </div>
          <div className="flex flex-col gap-2xs">
            <label htmlFor="native-video-prompt" className="text-title-xsmall text-sys-cont-neutral-light">추가 요청</label>
            <TextArea id="native-video-prompt" placeholder="원하는 움직임이나 연출을 적어 주세요." disabled={busy} value={customPrompt} maxLength={2000}
              onChange={event => setCustomPrompt(event.target.value)} className="min-h-7xl"/>
          </div>
        </div>
      </div>
      {(message || validation) && <p role="alert" className="text-body-small">{message || validation}</p>}
      <div className="flex items-center justify-end gap-2xs">
        <Button hierarchy="tertiary" size="small" disabled={busy} onClick={onClose}>취소</Button>
        <Button hierarchy="primary" size="small" onClick={submit} disabled={!preview || busy}>{busy ? '전달 중…' : '콘티 확인'}</Button>
      </div>
    </div>
  </ContentDialog>;
}

/** Only reveal this source video after it reaches the resolved source timestamp. */
function FramePreview({url, seconds}:{url:string;seconds:number}) {
  const [status, setStatus] = useState<'loading'|'ready'|'error'>('loading');
  return <div className="flex h-full flex-col gap-2xs">
    <video src={url} crossOrigin="anonymous" muted playsInline preload="auto" aria-label="선택한 컷의 원본 프레임" className={`min-h-0 flex-1 object-contain ${status === 'ready' ? '' : 'invisible'}`}
      onLoadedMetadata={event => {
        try {event.currentTarget.currentTime = seconds;}
        catch {setStatus('error');}
      }}
      onLoadedData={event => {if(!event.currentTarget.seeking && Math.abs(event.currentTarget.currentTime-seconds)<0.01) setStatus('ready');}}
      onSeeked={event => setStatus(Math.abs(event.currentTarget.currentTime-seconds)<0.01 ? 'ready' : 'error')}
      onError={() => setStatus('error')}/>
    <p role="status" className="text-body-small">{status === 'ready' ? `원본 ${seconds.toFixed(3)}초 프레임` : status === 'error' ? '프레임 미리보기를 불러오지 못했습니다. 콘티에서 원본을 확인해 주세요.' : '선택 프레임을 불러오는 중…'}</p>
  </div>;
}
